import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, HelpCircle, Wallet, Copy } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { getBscScanUrl } from "@/utils/network";
import { toast } from "sonner";
import { useAccount, useWalletClient } from 'wagmi';

interface TokenVerificationStatusProps {
  tokenAddress: string;
  chainId: number;
  tokenSymbol: string;
  tokenDecimals: number;
}

export function TokenVerificationStatus({ 
  tokenAddress, 
  chainId,
  tokenSymbol,
  tokenDecimals 
}: TokenVerificationStatusProps) {
  const bscScanUrl = `${getBscScanUrl(chainId)}/token/${tokenAddress}`;
  const { connector } = useAccount();
  const { data: walletClient } = useWalletClient();

  const addToWallet = async () => {
    try {
      // Try using wallet_watchAsset first (MetaMask and compatible wallets)
      if (walletClient) {
        try {
          const success = await walletClient.request({
            method: 'wallet_watchAsset',
            params: {
              type: 'ERC20',
              options: {
                address: tokenAddress,
                symbol: tokenSymbol,
                decimals: tokenDecimals,
              },
            },
          });
          
          if (success) {
            toast.success(`${tokenSymbol} was successfully added to your wallet`);
            return;
          }
        } catch (e) {
          // If wallet_watchAsset fails, continue to fallback
          console.log("wallet_watchAsset not supported, using fallback");
        }
      }

      // Fallback: Copy token address and show instructions
      await navigator.clipboard.writeText(tokenAddress);
      toast.success(
        <div className="space-y-2">
          <p>Token address copied to clipboard!</p>
          <p className="text-sm text-muted-foreground">To add manually:</p>
          <ol className="text-sm list-decimal list-inside space-y-1">
            <li>Open your wallet</li>
            <li>Find "Import Token" or "Add Custom Token"</li>
            <li>Paste the token address</li>
            <li>Verify the token details:</li>
            <ul className="list-disc list-inside ml-4 space-y-1">
              <li>Symbol: {tokenSymbol}</li>
              <li>Decimals: {tokenDecimals}</li>
            </ul>
          </ol>
        </div>,
        {
          duration: 10000,
        }
      );
    } catch (error) {
      toast.error('Error adding token to wallet. Please try adding it manually.');
      console.error(error);
    }
  };

  return (
    <Card className="border-yellow-500">
      <CardContent className="p-6">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <h2 className="text-2xl font-semibold">Token Contract Status</h2>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <HelpCircle className="h-5 w-5 text-muted-foreground" />
                </TooltipTrigger>
                <TooltipContent>
                  <p>View your token contract on BSCScan</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <div className="text-muted-foreground">
            <p>Contract Address:</p>
            <div className="flex items-center gap-2">
              <code className="text-sm">{tokenAddress}</code>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => {
                        navigator.clipboard.writeText(tokenAddress);
                        toast.success('Address copied to clipboard!');
                      }}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Copy address</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        </div>

        <div className="mt-4 space-y-2">
          <div className="flex items-center justify-between bg-green-500/10 p-3 rounded-md">
            <div className="flex items-center gap-2 text-green-500">
              <CheckCircle2 className="h-5 w-5" />
              <span>Contract is verified on BSCScan</span>
            </div>
            <Button
              variant="outline"
              className="px-3 h-8 bg-green-500 hover:bg-green-600 text-white border-0"
              onClick={() => window.open(bscScanUrl, '_blank')}
            >
              View
            </Button>
          </div>

          <Button
            onClick={addToWallet}
            className="w-full h-12 bg-gradient-to-r from-black via-yellow-500 to-black dark:from-gray-800 dark:via-yellow-500 dark:to-gray-800 hover:opacity-90 transition-opacity text-white"
          >
            <Wallet className="w-5 h-5 mr-2" />
            Add {tokenSymbol} to Wallet
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
